<template>
  <div class="sales-step-content">
    <div class="text-center mb-3">
      <font-awesome-icon icon="crown" size="3x" class="text-warning mb-2" />
      <h4>Plan auswählen</h4>
      <p class="text-muted mb-0">
        Wähle den passenden Plan für deinen ausgewählten Bereich
      </p>
    </div>

    <!-- Selected Team Summary -->
    <div class="mb-3">
      <h6>Ausgewählter Bereich:</h6>
      <div class="d-flex flex-wrap gap-2">
        <span class="badge bg-primary">
          {{ selectedTeam?.name }}
        </span>
      </div>
    </div>

    <!-- Plan Cards -->
    <div class="row g-3 justify-content-center" :class="{ 'plans-space-around': availablePlans.length === 2 }" v-if="availablePlans.length > 0">
      <div v-for="plan in availablePlans" :key="plan.type" class="col-md-6">
        <div 
          class="card plan-card h-100" 
          :class="getPlanCardClass(plan.type)"
          @click="selectPlan(plan)"
          style="cursor: pointer;"
        >
          <div class="card-header text-center">
            <h5 class="mb-0">
              <font-awesome-icon
                :icon="plan.type === 'team_pro' ? 'star' : 'crown'"
                class="me-2"
              />
              {{ plan.type === 'team_pro' ? 'Pro Plan' : 'Ultimate Plan' }}
            </h5>
          </div>
          <div class="card-body">
            <div v-if="plan.description" class="mb-3">
              <p class="text-muted">{{ plan.description }}</p>
            </div>

            <!-- Price Info -->
            <div class="text-center mb-4">
              <div class="h3 mb-1">{{ formatCurrency(plan.teamPlanPrice) }}</div>
              <small class="text-muted">pro Jahr</small>
              <div class="mt-2">
                <span class="badge bg-secondary">
                  {{ plan.teamPlanCoins }} Quittungen pro Monat
                </span>
              </div>
            </div>

            <!-- Features -->
            <div v-if="plan.teamPlanFeatures && plan.teamPlanFeatures.length > 0" class="mb-4">
              <h6 class="mb-3">Features:</h6>
              <ul class="list-unstyled">
                <li
                  v-for="feature in plan.teamPlanFeatures"
                  :key="feature"
                  class="mb-2 d-flex align-items-start"
                >
                  <font-awesome-icon icon="check" class="text-success me-2 mt-1" size="sm" />
                  <span>{{ feature }}</span>
                </li>
              </ul>
            </div>

            <!-- Action Button -->
            <div class="text-center">
              <button
                class="btn btn-primary w-100"
                :disabled="!canUpgrade"
                @click.stop="selectPlan(plan)"
              >
                <font-awesome-icon icon="arrow-up" class="me-2" />
                {{ plan.type === 'team_pro' ? 'Pro' : 'Ultimate' }} Plan wählen
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No Plans Available -->
    <div v-if="availablePlans.length === 0" class="text-center py-4">
      <div class="text-muted">
        <font-awesome-icon icon="info-circle" size="2x" class="mb-2" />
        <p class="mb-1">Derzeit sind keine Upgrade-Optionen verfügbar.</p>
        <p class="small mb-0">Bitte wende dich an den Support für weitere Informationen.</p>
      </div>
    </div>

    <!-- Coupon Input -->
    <CouponInput
      :description="'Hast du einen Gutscheincode? Gib ihn hier ein, um ein kostenloses Upgrade zu erhalten.'"
      :coupon-type="'team_upgrade'"
      :selected-plan="selectedPlan"
      :show-additional-info="true"
      @coupon-validated="onCouponValidated"
      @coupon-cleared="onCouponCleared"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { usePricing } from '../composables/usePricing'
import CouponInput from '../components/CouponInput.vue'

const props = defineProps({
  availablePlans: {
    type: Array,
    default: () => []
  },
  selectedTeam: {
    type: Object,
    default: null
  },
  selectedPlan: {
    type: Object,
    default: null
  },
  couponResult: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['plan-selected', 'coupon-validated', 'coupon-cleared'])

const { formatCurrency } = usePricing()

const canUpgrade = computed(() => {
  return props.selectedTeam?.status === 'free'
})

const getPlanCardClass = (planType) => {
  if (props.selectedPlan?.type === planType) {
    return 'border-primary'
  }
  return ''
}

const selectPlan = (plan) => {
  emit('plan-selected', plan)
}

const onCouponValidated = (result) => {
  emit('coupon-validated', result)
}

const onCouponCleared = () => {
  emit('coupon-cleared')
}
</script>

<style scoped>
.plan-card {
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.plan-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.plan-card.border-primary {
  border-color: var(--bs-primary) !important;
  background: rgba(13, 110, 253, 0.05);
}

.plans-space-around {
  justify-content: space-around !important;
}

.plans-space-around .col-md-6 {
  flex: 0 0 auto;
  max-width: 45%;
}
</style>
